import React, { useState, useEffect } from 'react';
import { get, post, put, openProtectedFile } from '../utils/api';
import ProspectOnboardingWorkflow from '../components/ProspectOnboardingWorkflow';

const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG');

const STATUS_COLORS = {
  draft:{ bg:'#F5F5F5', color:'#8A8A8A', label:'Draft' },
  offer_sent:{ bg:'#EDF3FF', color:'#3B5BDB', label:'Offer Sent' },
  offer_accepted:{ bg:'#E8F5EB', color:'#2E6B3A', label:'Offer Accepted' },
  offer_declined:{ bg:'#FDEAEA', color:'#8F2E24', label:'Offer Declined' },
  guarantor_pending:{ bg:'#FFF3DC', color:'#8B5E0A', label:'Guarantor Pending' },
  guarantor_approved:{ bg:'#D3F9D8', color:'#2B8A3E', label:'Guarantor Approved' },
  active:{ bg:'#556B2F22', color:'#556B2F', label:'Active Steward' },
  hired:{ bg:'#D3F9D8', color:'#2B8A3E', label:'Hired' },
  cancelled:{ bg:'#FDEAEA', color:'#8F2E24', label:'Cancelled' },
  offer_expired:{ bg:'#F5F5F5', color:'#8A8A8A', label:'Offer Expired' },
};

export default function Prospects() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [jobDescriptions, setJobDescriptions] = useState([]);
  const [showGuarantorModal, setShowGuarantorModal] = useState(false);
  const [showEmploymentModal, setShowEmploymentModal] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [checklistContent, setChecklistContent] = useState('');
  const [checklistTitle, setChecklistTitle] = useState('');
  const [secureDocumentId, setSecureDocumentId] = useState(null);
  const [sendingDoc, setSendingDoc] = useState(false);
  const [sentDocs, setSentDocs] = useState([]);
  const [showDocs, setShowDocs] = useState(false);
  const [resendingDocumentId, setResendingDocumentId] =
    useState(null);
  const [showNonCompeteModal, setShowNonCompeteModal] = useState(false);
  const [showIPModal, setShowIPModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [lastSentLink, setLastSentLink] = useState(null);
  const [biodataRecord, setBiodataRecord] = useState(null);
  const [showBiodata, setShowBiodata] = useState(false);
  const [payrollRecord, setPayrollRecord] = useState(null);
  const [showPayroll, setShowPayroll] = useState(false);
  const [payrollLink, setPayrollLink] = useState(null);
  const [roles, setRoles] = useState([]);
  const [assets, setAssets] = useState([]);
  const [docTemplates, setDocTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [documentFieldValues, setDocumentFieldValues] = useState({});
  const [showImportModal, setShowImportModal] = useState(false);
  const [importingDocument, setImportingDocument] = useState(false);
  const [importForm, setImportForm] = useState({
    template_id:'',
    signed_date:'',
    import_reason:
      'Historical signed employment document imported after secure workflow migration',
    verification_notes:'',
    file:null
  });
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [activationSaving, setActivationSaving] = useState(false);
  const [activationForm, setActivationForm] = useState({
    actual_start_date:'',
    reason:
      'Approved to begin compatibility testing after completion of employment documents and guarantor verification'
  });
  const [giftThreshold, setGiftThreshold] = useState('any gift of any value');
  const [assetForm, setAssetForm] = useState({
    asset_id:'', asset_description:'', asset_serial_number:'',
    asset_accessories:'', asset_condition_at_issue:'Good',
    asset_issue_date:'', asset_expected_return_date:'',
    asset_authorised_purpose:'', asset_issued_by:'Doris Gideon-Ayantola'
  });
  const [nonCompeteForm, setNonCompeteForm] = useState({ restriction_period_months: '12' });
  const [ipForm, setIpForm] = useState({ pre_existing_material_schedule: 'None' });
  const [empForm, setEmpForm] = useState({
    work_location: 'No. 3, S. Ade-Ojo Estate, Sawmill, Old Ife Road, Ibadan, Oyo State, Nigeria',
    steward_residential_address:'',
    probation_notice_period:'1 week',
    confirmed_steward_notice_period:'1 month',
  });
  const [guarantorForm, setGuarantorForm] = useState({ guarantor_name:'', guarantor_email:'', guarantor_phone:'', alternative_email:'' });
  const [verifyForm, setVerifyForm] = useState({ decision:'', notes:'' });
  const [showVerify, setShowVerify] = useState(false);
  const [form, setForm] = useState({
    full_name:'', email:'', phone:'', position:'', department:'',
    reports_to:'', proposed_start_date:'', monthly_salary:'',
    work_location:'Ibadan, Nigeria', probation_period:'3 months',
    employment_category:'Full-time', expiry_date:'', notes:'',
    auditor_payment_terms:'', auditor_assessment_terms:'',
    auditor_notice_benefits_terms:''
  });

  const isAuditorProspect =
    selected?.onboarding?.role === 'inventory_auditor' ||
    String(selected?.position || '')
      .trim()
      .toLowerCase() === 'inventory auditor';

  const eligibleDocTemplates = docTemplates.filter(template =>
    isAuditorProspect
      ? ![
          '8c870ee0-78f0-45a9-87d1-872f653373f0',
          'eba8a604-9813-4d2f-9352-39190a20a900'
        ].includes(String(template.id))
      : String(template.id) !==
          '8884bebd-b81d-4f47-b212-1f7b6c834792'
  );

  useEffect(() => {
    load();
    get('/job-descriptions').then(d => setJobDescriptions((d.data||[]).filter(j => j.status==='published' || j.is_active)));
    get('/documents/templates?recipient_type=prospect')
      .then(d => setDocTemplates(d.data||[]))
      .catch(error=>{setDocTemplates([]);alert('Document choices could not be loaded: '+error.message);});
    get('/roles').then(d => setRoles(d.data||[])).catch(()=>{});
    get('/assets').then(d => setAssets((d.data||[]).filter(a=>a.status==='active' && !a.assigned_to))).catch(()=>{});
  }, []);

  const load = () => {
    setLoading(true);
    get('/prospects').then(d => setProspects(d.data||[])).finally(() => setLoading(false));
  };

  const loadSentDocs = (id) => {
    get('/prospects/'+id+'/documents').then(d=>setSentDocs(d.data||[])).catch(()=>{});
    get('/biodata/prospect/'+id).then(d=>setBiodataRecord(d.data||null)).catch(()=>setBiodataRecord(null));
    get('/biodata/payroll/prospect/'+id).then(d=>setPayrollRecord(d.data||null)).catch(()=>setPayrollRecord(null));
  };
  const resendSecureDocument = async document => {
    const retry = document?.secure_workflow && document.status === 'ready' &&
      document.company_authorized === true && Boolean(document.last_delivery_error);
    if (!document?.secure_workflow || (!retry && !['sent','viewed'].includes(document.status))) {
      alert('This document is not eligible for delivery retry or resend.');
      return;
    }
    if (!window.confirm(retry
      ? 'Retry delivery of the existing authorized document "' + document.document_title +
        '" to ' + document.sent_to + '? Its existing company signature will be retained.'
      : 'Resend ' + document.document_title + ' to ' + document.sent_to +
        '? The existing secure link will be extended for seven days.')) return;

    setResendingDocumentId(
      document.id
    );

    try {
      const response = await post(
        '/documents/' +
          document.id +
          (retry ? '/authorize-send' : '/resend-email'),
        retry ? {confirm_company_signature:true} : {}
      );

      const refreshed = await get(
        '/prospects/' +
          selected.id +
          '/documents'
      );

      setSentDocs(
        refreshed.data || []
      );

      alert(
        response.message ||
        (retry ? 'Document delivery retry succeeded.' : 'Document reminder sent successfully.')
      );
    } catch (error) {
      alert(
        error.message ||
        'The document reminder could not be sent.'
      );
    } finally {
      setResendingDocumentId(null);
    }
  };

  const loadSelected = (id) => {
    get('/prospects/'+id).then(d => setSelected(d));
    get('/prospects/'+id+'/documents').then(d=>setSentDocs(d.data||[])).catch(()=>{});
    get('/biodata/prospect/'+id).then(d=>setBiodataRecord(d.data||null)).catch(()=>setBiodataRecord(null));
    get('/biodata/payroll/prospect/'+id).then(d=>setPayrollRecord(d.data||null)).catch(()=>setPayrollRecord(null));
  };

  const create = async () => {
    if (!form.full_name || !form.email || !form.position) {
      alert('Name, email and position required'); return;
    }
    if (form.position.trim().toLowerCase() === 'inventory auditor' &&
        (form.employment_category !== 'Contract' || !form.proposed_start_date ||
         !form.expiry_date || !(Number(form.monthly_salary) > 0) ||
         !form.auditor_payment_terms.trim() ||
         !form.auditor_assessment_terms.trim() ||
         !form.auditor_notice_benefits_terms.trim())) {
      alert('Complete all Inventory Auditor offer terms before creating this prospect.');
      return;
    }
    setSaving(true);
    try {
      await post('/prospects', form);
      setShowForm(false);
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const sendOffer = async (id) => {
    const title = selected?.position?.trim().toLowerCase() === 'inventory auditor'
      ? 'Inventory Auditor Offer Letter' : 'Standard Offer Letter';
    if (!window.confirm('Send ' + title + ' to applicant?')) return;
    setSaving(true);
    try {
      const result = await post('/prospects/'+id+'/send-offer', {});
      alert('Offer sent! Portal link: ' + result.portal_url);
      loadSelected(id); load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const sendGuarantor = async (id) => {
    if (!guarantorForm.guarantor_name || !guarantorForm.guarantor_email) {
      alert('Guarantor name and email required'); return;
    }
    setSaving(true);
    try {
      const result = await post('/prospects/'+id+'/send-guarantor', guarantorForm);
      alert('Guarantor invitation sent! Link: ' + result.portal_url);
      setShowGuarantorModal(false);
      loadSelected(id); load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const verifyGuarantor = async (id) => {
    if (!verifyForm.decision) { alert('Select a decision'); return; }
    setSaving(true);
    try {
      await put('/prospects/'+id+'/verify-guarantor', verifyForm);
      setShowVerify(false);
      loadSelected(id); load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="page">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',color:'#2C1F16',margin:'0 0 4px'}}>Prospects</h2>
          <p style={{color:'#8A8A8A',fontSize:13,margin:0}}>Manage applicants through the offer and onboarding process</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          {selected && <button className="btn btn-ghost" onClick={()=>setSelected(null)}>Back</button>}
          {!selected && <button className="btn btn-primary btn-sm" onClick={()=>setShowForm(true)}>+ New Prospect</button>}
        </div>
      </div>

      {/* Status filter buttons */}
      {!loading && prospects.length > 0 && (
        <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap'}}>
          <button onClick={()=>setStatusFilter('')}
            style={{padding:'5px 12px',borderRadius:20,border:'2px solid '+(statusFilter===''?'#556B2F':'#E8E0D4'),
              background:statusFilter===''?'#556B2F':'#fff',color:statusFilter===''?'#fff':'#3B2A21',
              fontSize:11,fontWeight:600,cursor:'pointer'}}>
            All ({prospects.length})
          </button>
          {Object.entries(STATUS_COLORS).map(([key,val])=>{
            const count = prospects.filter(p=>p.status===key).length;
            if (!count) return null;
            return (
              <button key={key} onClick={()=>setStatusFilter(statusFilter===key?'':key)}
                style={{padding:'5px 12px',borderRadius:20,
                  border:'2px solid '+(statusFilter===key?val.color:'#E8E0D4'),
                  background:statusFilter===key?val.bg:'#fff',color:val.color,
                  fontSize:11,fontWeight:600,cursor:'pointer'}}>
                {val.label} ({count})
              </button>
            );
          })}
        </div>
      )}
      {!selected ? (
        loading ? <div className="spinner"/> : prospects.length === 0 ? (
          <div className="empty-state">
            <h3>No prospects yet</h3>
            <p>Click + New Prospect to start the offer process</p>
          </div>
        ) : (
          <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'#2C1F16'}}>
                  {['Ref','Name','Position','Offer','Guarantor','Status',''].map(h=>(
                    <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:9,
                      color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em',fontWeight:700}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {prospects.filter(p=>!statusFilter||p.status===statusFilter).map((p,i)=>{
                  const st = STATUS_COLORS[p.status]||STATUS_COLORS.draft;
                  return (
                    <tr key={p.id} style={{background:i%2===0?'#fff':'#F9F8F5',cursor:'pointer'}}
                      onClick={()=>loadSelected(p.id)}>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                        color:'#A8893D',fontWeight:600,fontFamily:'monospace'}}>{p.prospect_ref}</td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',fontWeight:600}}>{p.full_name}</td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{p.position}</td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                        <span style={{fontSize:9,padding:'2px 8px',borderRadius:20,fontWeight:600,
                          background:p.offer_status==='accepted'?'#E8F5EB':p.offer_status==='sent'?'#EDF3FF':'#F5F5F5',
                          color:p.offer_status==='accepted'?'#2E6B3A':p.offer_status==='sent'?'#3B5BDB':'#8A8A8A'}}>
                          {p.offer_status||'draft'}
                        </span>
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                        <span style={{fontSize:9,padding:'2px 8px',borderRadius:20,fontWeight:600,
                          background:p.guarantor_status==='approved'?'#E8F5EB':p.guarantor_status==='submitted'?'#FFF3DC':'#F5F5F5',
                          color:p.guarantor_status==='approved'?'#2E6B3A':p.guarantor_status==='submitted'?'#8B5E0A':'#8A8A8A'}}>
                          {p.guarantor_status||'none'}
                        </span>
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                        <span style={{fontSize:9,padding:'3px 10px',borderRadius:20,fontWeight:600,
                          background:st.bg,color:st.color}}>{st.label}</span>
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#A8893D'}}>→</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div>
          <div style={{background:'#3B2A21',borderRadius:12,padding:20,marginBottom:20}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
              <div>
                <div style={{color:'#A8893D',fontSize:11,fontFamily:'monospace',marginBottom:4}}>{selected.prospect_ref}</div>
                <div style={{color:'#F2EFE7',fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',fontWeight:600}}>{selected.full_name}</div>
                <div style={{color:'rgba(242,239,231,.5)',fontSize:12,marginTop:4}}>{selected.position} · {selected.department||'—'}</div>
              </div>
              <span style={{fontSize:10,padding:'4px 12px',borderRadius:20,fontWeight:700,
                background:(STATUS_COLORS[selected.status]||STATUS_COLORS.draft).bg,
                color:(STATUS_COLORS[selected.status]||STATUS_COLORS.draft).color}}>
                {(STATUS_COLORS[selected.status]||STATUS_COLORS.draft).label}
              </span>
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:10,marginBottom:20}}>
            {[
              ['Email', selected.email],
              ['Phone', selected.phone||'—'],
              ['Start Date', selected.proposed_start_date ? new Date(selected.proposed_start_date).toLocaleDateString('en-NG') : '—'],
              ['Salary', selected.monthly_salary ? fmt(selected.monthly_salary)+'/month' : '—'],
              ['Location', selected.work_location],
              ['Probation', selected.probation_period],
              ['Category', selected.employment_category],
              ['Reports To', selected.reports_to||'—'],
              ['Offer Letter', selected.offer?.offer_template_version
                ? ((selected.position||'').trim().toLowerCase()==='inventory auditor'
                    ? 'Inventory Auditor Offer Letter' : 'Standard Offer Letter')
                    + ' v' + selected.offer.offer_template_version
                : 'Not issued'],
            ].map(([label,val])=>(
              <div key={label} style={{background:'#F9F8F5',borderRadius:8,padding:12}}>
                <div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:3}}>{label}</div>
                <div style={{fontSize:12,color:'#2C1F16',fontWeight:500}}>{val}</div>
              </div>
            ))}
          </div>

          {(selected.position||'').trim().toLowerCase()==='inventory auditor' && (
            <div style={{background:'#F9F8F5',borderRadius:12,padding:16,marginBottom:20}}>
              <strong>Inventory Auditor offer terms</strong>
              <p>Payment and partial first month: {selected.offer?.auditor_payment_terms||'Not entered'}</p>
              <p>Assessment: {selected.offer?.auditor_assessment_terms||'Not entered'}</p>
              <p>Notice and benefits: {selected.offer?.auditor_notice_benefits_terms||'Not entered'}</p>
            </div>
          )}
          <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,padding:20,marginBottom:20}}>
            <div style={{fontSize:11,fontWeight:700,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:16}}>Actions</div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {selected.status === 'draft' && selected.offer?.status === 'draft' && (
                <button className="btn btn-primary btn-sm" onClick={()=>sendOffer(selected.id)} disabled={saving}>
                  Send Offer Letter
                </button>
              )}
              {['offer_sent','offer_expired'].includes(selected.status) && (
                <button className="btn btn-outline btn-sm" style={{color:'#8F2E24',borderColor:'#8F2E24'}}
                  onClick={async()=>{
                    if (!window.confirm('Resend offer to '+selected.email+'? This will extend the expiry by 7 days.')) return;
                    try {
                      const r = await post('/prospects/'+selected.id+'/resend-offer', {});
                      alert('Offer resent successfully. New link valid for 7 days.');
                      load(); loadSelected(selected.id);
                    } catch(e) { alert(e.message); }
                  }}>🔁 Resend Offer Link</button>
              )}
              {['offer_accepted','onboarding'].includes(selected.status) && !selected.guarantor && (
                <button className="btn btn-outline btn-sm" style={{color:'#2E6B3A',borderColor:'#2E6B3A'}}
                  onClick={()=>{
                    setGuarantorForm({guarantor_name:'',guarantor_email:'',guarantor_phone:''});
                    setShowGuarantorModal(true);
                  }}>
                  Send Guarantor Invitation
                </button>
              )}
              {selected.guarantor && (
                <button className="btn btn-outline btn-sm" style={{color:'#8B5E0A',borderColor:'#8B5E0A'}}
                  onClick={()=>{
                    setGuarantorForm({
                      guarantor_name: selected.guarantor.full_name||'',
                      guarantor_email: selected.guarantor.email||'',
                      guarantor_phone: selected.guarantor.phone||'',
                      alternative_email: ''
                    });
                    setShowGuarantorModal(true);
                  }}>
                  ✏️ Edit / Resend Guarantor
                </button>
              )}
              {selected.guarantor && selected.guarantor.status==='submitted' && (
                <button className="btn btn-outline btn-sm" style={{color:'#3B5BDB',borderColor:'#3B5BDB'}}
                  onClick={()=>setShowVerify(true)}>
                  Verify Guarantor
                </button>
              )}
              {lastSentLink && (
                <div style={{background:'#E8F5EB',border:'1px solid #C3E6CB',borderRadius:8,padding:10,fontSize:11,marginBottom:8}}>
                  <div style={{fontWeight:700,color:'#2E6B3A',marginBottom:4}}>📋 Last Document Link — copy to share:</div>
                  <div style={{display:'flex',gap:6,alignItems:'center'}}>
                    <input readOnly value={lastSentLink} style={{flex:1,fontSize:10,padding:'4px 8px',border:'1px solid #C3E6CB',borderRadius:4,background:'#fff'}} onClick={e=>e.target.select()} />
                    <button className="btn btn-primary btn-sm" onClick={()=>{navigator.clipboard.writeText(lastSentLink);alert('Link copied!');}}>Copy</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setLastSentLink(null)}>✕</button>
                  </div>
                </div>
              )}
              {selected.status === 'cancelled' && (
                <div style={{background:'#FDEAEA',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#8F2E24',marginBottom:8,fontWeight:600}}>
                  ⚠ This prospect has been cancelled. No further actions can be taken.
                </div>
              )}
              {!['hired','cancelled'].includes(selected.status) && (
                <button className="btn btn-outline btn-sm" style={{color:'#8F2E24',borderColor:'#8F2E24'}}
                  onClick={()=>{setCancelReason('');setShowCancelModal(true);}}>
                  ✕ Cancel Prospect
                </button>
              )}
              {selected.status !== 'cancelled' && (
                <button className="btn btn-outline btn-sm" style={{color:'#3B5BDB',borderColor:'#3B5BDD'}}
                  onClick={()=>{
                      setSelectedTemplate('');
                      setDocumentFieldValues({});
                      setShowDocModal(true);
                    }}>
                📄 Send Document
                </button>
              )}
              {selected.onboarding && !selected.onboarding.activated_user_id && (
                <button
                  className="btn btn-outline btn-sm"
                  style={{
                    color:'#6B3FA0',
                    borderColor:'#6B3FA0'
                  }}
                  onClick={()=>{
                    setImportForm({
                      template_id:'',
                      signed_date:'',
                      import_reason:
                        'Historical signed employment document imported after secure workflow migration',
                      verification_notes:'',
                      file:null
                    });
                    setShowImportModal(true);
                  }}
                >
                  📎 Import Signed Document
                </button>
              )}
              {selected.onboarding && !selected.onboarding.activated_user_id && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={()=>{
                    setActivationForm({
                      actual_start_date:
                        (isAuditorProspect
                          ? selected.onboarding.proposed_start_date || selected.proposed_start_date
                          : selected.onboarding.compatibility_test_start
                        )?.slice(0,10) || '',
                      reason: isAuditorProspect
                        ? 'Inventory Auditor onboarding after acceptance of the role-specific offer; NDA and Privacy Agreement are required before account activation'
                        : 'Approved to begin compatibility testing after completion of employment documents and guarantor verification'
                    });
                    setShowActivationModal(true);
                  }}
                >
                  {selected.onboarding.activation_status==='pending_md'
                    ? 'Review Activation'
                    : isAuditorProspect ? 'Contract Activation' : 'Compatibility Activation'}
                </button>
              )}
              {payrollRecord ? (
                <button className="btn btn-outline btn-sm" style={{color:'#1864AB',borderColor:'#1864AB'}}
                  onClick={()=>setShowPayroll(true)}>
                  💳 View Payroll Details
                </button>
              ) : (
                <button className="btn btn-outline btn-sm" style={{color:'#1864AB',borderColor:'#1864AB'}}
                  onClick={async()=>{
                    try {
                      const r = await post('/biodata/payroll/send/'+selected.id, {});
                      setPayrollLink(r.portal_url);
                      alert('Payroll form sent to '+selected.full_name);
                    } catch(e) { alert(e.message); }
                  }}>
                  💳 Send Payroll Form
                </button>
              )}
              {payrollLink && (
                <div style={{background:'#E7F5FF',borderRadius:8,padding:'8px 12px',fontSize:11,marginTop:4}}>
                  <strong>Payroll Link:</strong>
                  <input readOnly value={payrollLink} style={{marginLeft:8,fontSize:10,padding:'2px 6px',border:'1px solid #ccc',borderRadius:4,width:300}} onClick={e=>e.target.select()} />
                  <button className="btn btn-sm btn-primary" style={{marginLeft:6}} onClick={()=>{navigator.clipboard.writeText(payrollLink);alert('Copied!');}}>Copy</button>
                </div>
              )}
              {biodataRecord && (
                <button className="btn btn-outline btn-sm" style={{color:'#6B3FA0',borderColor:'#6B3FA0'}}
                  onClick={()=>setShowBiodata(true)}>
                  📋 View Bio-Data
                </button>
              )}
              {selected.status==='hired' && (
                <button className="btn btn-outline btn-sm" style={{color:'#556B2F',borderColor:'#556B2F'}}
                  onClick={async()=>{
                    try {
                      const r = await post('/biodata/send/'+selected.id, {});
                      alert('Bio-Data form link sent to '+selected.email);
                    } catch(e) { alert(e.message); }
                  }}>
                  📋 Send Bio-Data Form
                </button>
              )}
              {selected.guarantor?.status==='approved' && !isAuditorProspect && (
                <button className="btn btn-outline btn-sm" style={{color:'#556B2F',borderColor:'#556B2F'}}
                  onClick={()=>setShowEmploymentModal(true)}>
                  📄 Generate Employment Agreement
                </button>
              )}
              {selected.guarantor?.status==='approved' && !isAuditorProspect && (
                <button className="btn btn-outline btn-sm" style={{color:'#3B2A21',borderColor:'#3B2A21'}}
                  onClick={()=>setShowNonCompeteModal(true)}>
                  📄 Generate Non-Compete Agreement
                </button>
              )}
              {selected.guarantor?.status==='approved' && !isAuditorProspect && (
                <button className="btn btn-outline btn-sm" style={{color:'#8F2E24',borderColor:'#8F2E24'}}
                  onClick={()=>setShowIPModal(true)}>
                  📄 Generate IP Assignment Agreement
                </button>
              )}
              {(selected.guarantor?.status==='approved' || isAuditorProspect) && !selected.onboarding && selected.status!=='hired' && (
                <button className="btn btn-primary btn-sm"
                  onClick={async()=>{
                    if (!window.confirm('Start onboarding for '+selected.full_name+'? This will mark the prospect as hired and create an onboarding record.')) return;
                    try {
                      const r = await post('/onboarding/from-prospect/'+selected.id, {});
                      alert('Onboarding record created successfully! '+r.data.checklist_items_created+' checklist items generated.');
                      setSelected({...selected, status:'hired'});
                    } catch(e) { alert(e.message); }
                  }}>
                  🎯 Start Onboarding
                </button>
              )}
              <button className="btn btn-outline btn-sm" style={{color:'#3B5BDB',borderColor:'#3B5BDB'}}
                onClick={()=>setShowDocs(true)}>
                📄 Documents Sent ({sentDocs.length})
              </button>

              {payrollLink && (
                <div style={{background:'#E7F5FF',borderRadius:8,padding:'8px 12px',fontSize:11,marginTop:4}}>
                  <strong>Payroll Link:</strong>
                  <input readOnly value={payrollLink} style={{marginLeft:8,fontSize:10,padding:'2px 6px',border:'1px solid #ccc',borderRadius:4,width:300}} onClick={e=>e.target.select()} />
                  <button className="btn btn-sm btn-primary" style={{marginLeft:6}} onClick={()=>{navigator.clipboard.writeText(payrollLink);alert('Copied!');}}>Copy</button>
                </div>
              )}
              {selected.status==='hired' && (
                <span style={{fontSize:12,padding:'6px 12px',borderRadius:6,
                  background:'#E8F5EB',color:'#2E6B3A',fontWeight:600}}>
                  ✅ Onboarding Started
                </span>
              )}
              <button className="btn btn-ghost btn-sm"
                onClick={async()=>{
                  try {
                    const r = await get('/prospects/'+selected.id+'/checklist');
                    setChecklistContent(r.data.merged_content);
                    setShowChecklist(true);
                  } catch(e) { alert(e.message); }
                }}>
                📋 View Checklist
              </button>
            </div>
          </div>

          {selected.offer && (
            <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,padding:20,marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:12}}>Offer</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
                {[
                  ['Status', selected.offer.status],
                  ['Sent', selected.offer.sent_at ? new Date(selected.offer.sent_at).toLocaleDateString('en-NG') : '—'],
                  ['Response', selected.offer.accepted_at ? 'Accepted' : selected.offer.declined_at ? 'Declined' : 'Pending'],
                ].map(([label,val])=>(
                  <div key={label} style={{background:'#F9F8F5',borderRadius:8,padding:10}}>
                    <div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:3}}>{label}</div>
                    <div style={{fontSize:12,fontWeight:500,color:'#2C1F16',textTransform:'capitalize'}}>{val}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selected.guarantor && (
            <GuarantorDetailPanel prospectId={selected.id} guarantorSummary={selected.guarantor} />
          )}
        </div>
      )}

      {showPayroll && payrollRecord && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-hdr" style={{background:'#1864AB',borderRadius:'10px 10px 0 0'}}>
              <span className="modal-title" style={{color:'#fff'}}>💳 Payroll Details — {selected.full_name}</span>
              <button className="btn btn-ghost btn-sm" style={{color:'#fff'}} onClick={()=>setShowPayroll(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#E7F5FF',borderRadius:8,padding:'8px 14px',fontSize:12,color:'#1864AB',marginBottom:16}}>
                Submitted: {new Date(payrollRecord.submitted_at).toLocaleDateString('en-NG',{day:'numeric',month:'long',year:'numeric'})}
              </div>
              {[
                ['Bank Name', payrollRecord.bank_name],
                ['Account Name', payrollRecord.account_name],
                ['Account Number', payrollRecord.account_number],
                ['Account Type', payrollRecord.account_type],
                ['Bank Code', payrollRecord.bank_code],
                ['Name Difference Explanation', payrollRecord.name_difference_explanation],
              ].filter(([,v])=>v).map(([label,value])=>(
                <div key={label} style={{display:'flex',borderBottom:'1px solid #F2EFE7',padding:'10px 0'}}>
                  <div style={{width:'45%',fontSize:11,color:'#8A8A8A',fontWeight:600,textTransform:'uppercase'}}>{label}</div>
                  <div style={{width:'55%',fontSize:13,fontWeight:600,color:'#2C1F16'}}>{value}</div>
                </div>
              ))}
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowPayroll(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {showBiodata && biodataRecord && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:700,maxHeight:'90vh',overflowY:'auto'}}>
            <div className="modal-hdr" style={{background:'#6B3FA0',borderRadius:'10px 10px 0 0',position:'sticky',top:0,zIndex:10}}>
              <span className="modal-title" style={{color:'#fff'}}>📋 Bio-Data — {selected.full_name}</span>
              <button className="btn btn-ghost btn-sm" style={{color:'#fff'}} onClick={()=>setShowBiodata(false)}>✕</button>
            </div>
            <div className="modal-body">
              {(() => {
                const d = biodataRecord.form_data || {};
                const fmtDate = v => v ? new Date(v).toLocaleDateString('en-NG',{day:'numeric',month:'long',year:'numeric'}) : '—';
                const Row = ({label,value}) => (
                  <div style={{display:'flex',borderBottom:'1px solid #F2EFE7',padding:'8px 0'}}>
                    <div style={{width:'40%',fontSize:11,color:'#8A8A8A',fontWeight:600,textTransform:'uppercase',letterSpacing:'.04em'}}>{label}</div>
                    <div style={{width:'60%',fontSize:13,color:'#2C1F16'}}>{value||'—'}</div>
                  </div>
                );
                const Section = ({title}) => (
                  <div style={{background:'#556B2F',color:'#F2EFE7',fontSize:11,fontWeight:700,padding:'6px 12px',borderRadius:6,margin:'16px 0 8px',textTransform:'uppercase',letterSpacing:'.06em'}}>{title}</div>
                );
                return (
                  <div>
                    <div style={{background:'#E8F5EB',borderRadius:8,padding:'8px 14px',fontSize:12,color:'#2E6B3A',marginBottom:16}}>
                      Submitted: {fmtDate(biodataRecord.submitted_at)}
                    </div>
                    <Section title="Personal Information" />
                    <Row label="Title" value={d.title} />
                    <Row label="Full Name" value={selected.full_name} />
                    <Row label="Sex" value={d.sex} />
                    <Row label="Date of Birth" value={fmtDate(d.date_of_birth)} />
                    <Row label="Place of Birth" value={d.place_of_birth} />
                    <Row label="Nationality" value={d.nationality} />
                    <Row label="State of Origin" value={d.state_of_origin} />
                    <Row label="LGA" value={d.lga} />
                    <Row label="Marital Status" value={d.marital_status} />
                    <Row label="Maiden Name" value={d.maiden_name} />
                    <Row label="Phone" value={d.phone} />
                    <Row label="Email" value={d.email} />
                    <Row label="Permanent Address" value={d.permanent_address} />
                    <Row label="Residential Address" value={d.residential_address} />
                    <Section title="Health" />
                    <Row label="Blood Group" value={d.blood_group} />
                    <Row label="Genotype" value={d.genotype} />
                    <Row label="Health History" value={d.health_history} />
                    <Section title="Education" />
                    {(d.educational_history||[]).filter(e=>e.degree).map((e,i)=>(
                      <Row key={i} label={e.degree} value={e.institution+' ('+e.year+')'} />
                    ))}
                    {(d.professional_qualifications||[]).filter(p=>p.body).map((p,i)=>(
                      <Row key={i} label={p.body} value={p.grade+' ('+p.year+')'} />
                    ))}
                    <Section title="Employment History" />
                    {(d.employment_history||[]).filter(e=>e.name).map((e,i)=>(
                      <div key={i} style={{background:'#F9F8F5',borderRadius:8,padding:10,marginBottom:8,fontSize:12}}>
                        <div style={{fontWeight:700,color:'#3B2A21'}}>{e.name}</div>
                        <div style={{color:'#8A8A8A'}}>{e.position} · {e.date_from} to {e.date_to}</div>
                        <div style={{color:'#556B2F',marginTop:4}}>{e.description}</div>
                      </div>
                    ))}
                    <Section title="Family" />
                    <Row label="Spouse Name" value={d.spouse_name} />
                    <Row label="Spouse Phone" value={d.spouse_phone} />
                    <Row label="Spouse Email" value={d.spouse_email} />
                    <Row label="Father" value={d.father_name+' · '+d.father_phone} />
                    <Row label="Mother" value={d.mother_name+' · '+d.mother_phone} />
                    {(d.children||[]).filter(c=>c.name).map((c,i)=>(
                      <Row key={i} label={'Child '+(i+1)} value={c.name+' · '+c.sex+' · '+fmtDate(c.dob)} />
                    ))}
                    <Section title="Spouse Details" />
                    <Row label="Spouse Name" value={d.spouse_name} />
                    <Row label="Spouse Maiden Name" value={d.spouse_maiden_name} />
                    <Row label="Spouse Phone" value={d.spouse_phone} />
                    <Row label="Spouse Email" value={d.spouse_email} />
                    <Row label="Spouse Address" value={d.spouse_address} />
                    <Row label="Spouse Contact Address" value={d.spouse_contact_address} />
                    {(d.children||[]).filter(c=>c.name).length > 0 && (
                      <>
                        <Section title="Children" />
                        {(d.children||[]).filter(c=>c.name).map((c,i)=>(
                          <Row key={i} label={'Child '+(i+1)} value={c.name+' · '+c.sex+' · '+fmtDate(c.dob)} />
                        ))}
                      </>
                    )}
                    <Section title="Parents" />
                    <Row label="Father" value={(d.father_name||'—')+' · '+(d.father_phone||'—')} />
                    <Row label="Mother" value={(d.mother_name||'—')+' · '+(d.mother_phone||'—')} />
                    <Section title="Emergency Contact" />
                    <Row label="Name" value={d.emergency_contact_name} />
                    <Row label="Phone" value={d.emergency_contact_phone} />
                    <Row label="Email" value={d.emergency_contact_email} />
                    <Row label="Relationship" value={d.emergency_contact_relationship} />
                    <Section title="Criminal Background" />
                    <Row label="Ever convicted of a crime?" value={d.criminal_q1==='yes'?'⚠ YES':'No'} />
                    <Row label="Any pending criminal case?" value={d.criminal_q2==='yes'?'⚠ YES':'No'} />
                    {(d.criminal_q1==='yes'||d.criminal_q2==='yes') && (
                      <Row label="Details" value={d.criminal_details} />
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="modal-ftr" style={{gap:8,flexWrap:'wrap'}}>
              <button className="btn btn-outline" onClick={()=>setShowBiodata(false)}>Close</button>
              {biodataRecord?.review_status !== 'approved' && (
                <button className="btn btn-primary" onClick={async()=>{
                  await post('/biodata/review/'+biodataRecord.id, {decision:'approved'});
                  setBiodataRecord({...biodataRecord, review_status:'approved'});
                  alert('Bio-data approved');
                }}>✓ Approve Bio-Data</button>
              )}
              {biodataRecord?.review_status !== 'flagged' && (
                <button className="btn btn-outline" style={{color:'#8F2E24',borderColor:'#8F2E24'}} onClick={async()=>{
                  const note = window.prompt('What issue did you find?');
                  if (!note) return;
                  await post('/biodata/review/'+biodataRecord.id, {decision:'flagged', note});
                  setBiodataRecord({...biodataRecord, review_status:'flagged', review_note:note});
                  alert('Bio-data flagged');
                }}>⚠ Flag Issue</button>
              )}
            </div>
          </div>
        </div>
      )}
      {showCancelModal && selected && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:460}}>
            <div className="modal-hdr" style={{background:'#8F2E24',borderRadius:'10px 10px 0 0'}}>
              <span className="modal-title" style={{color:'#fff'}}>Cancel Prospect</span>
              <button className="btn btn-ghost btn-sm" style={{color:'#fff'}} onClick={()=>setShowCancelModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#FDEAEA',borderRadius:8,padding:10,fontSize:12,color:'#8F2E24',marginBottom:14}}>
                This will mark {selected.full_name} as cancelled and send them an email explaining why.
              </div>
              <div className="form-group">
                <label className="form-label">Select a Reason Template</label>
                <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:10}}>
                  {[
                    ['Offer Expired', 'Your offer of employment has expired as it was not accepted within the stipulated timeframe. If you remain interested in joining Bloans Creations Ltd., please reach out to HR to discuss your options.'],
                    ['Not Suitable for Role', 'After careful evaluation of your application and our compatibility assessment, we have concluded that your profile is not the right fit for the role at this time. We appreciate the effort you put into the process and wish you success in your career journey.'],
                    ['Position Filled', 'The position you applied for has been filled internally. We are grateful for your interest in Bloans Creations Ltd. and encourage you to apply again when a suitable opportunity arises.'],
                    ['Failed Background Check', 'Following our standard verification process, we are unable to proceed with your application at this time. We appreciate your interest and wish you well.'],
                    ['Withdrew Application', 'We have noted your withdrawal from our recruitment process. We appreciate your interest in Bloans Creations Ltd. and wish you all the best in your future endeavours.'],
                  ].map(([label, text]) => (
                    <button key={label} type="button"
                      className={'btn btn-sm '+(cancelReason===text?'btn-primary':'btn-outline')}
                      style={{fontSize:11}}
                      onClick={()=>setCancelReason(text)}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Reason for Cancellation *</label>
                <textarea className="form-control" rows={4} value={cancelReason}
                  onChange={e=>setCancelReason(e.target.value)}
                  placeholder="Select a template above or write a custom reason..." />
                <div className="form-hint">This reason will be included in the email to the prospect. You can edit the template text above.</div>
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowCancelModal(false)}>Go Back</button>
              <button className="btn btn-danger" disabled={!cancelReason.trim()||saving} onClick={async()=>{
                if (!window.confirm('Cancel this prospect and send them an email?')) return;
                setSaving(true);
                try {
                  await post('/prospects/'+selected.id+'/cancel', {reason: cancelReason});
                  setShowCancelModal(false);
                  setCancelReason('');
                  load();
                  loadSelected(selected.id);
                } catch(e) { alert(e.message); }
                finally { setSaving(false); }
              }}>{saving?'Cancelling...':'Cancel Prospect & Send Email'}</button>
            </div>
          </div>
        </div>
      )}
      {showDocModal && selected && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-hdr">
              <span className="modal-title">Send Document</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowDocModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Select Document *</label>
                <select
                    className="form-control"
                    value={selectedTemplate}
                    onChange={event=>{
                      const title=
                        event.target.value;

                      const template=
                        docTemplates.find(
                          item =>
                            item.title === title
                        );

                      const values={};

                      (
                        template?.input_fields ||
                        []
                      ).forEach(field=>{
                        values[field.field_key]=
                          field.default_value ||
                          '';
                      });

                      setSelectedTemplate(title);
                      setDocumentFieldValues(
                        values
                      );
                    }}
                  >
                    <option value="">
                      — Choose a document —
                    </option>

                    {eligibleDocTemplates.map(
                      template=>(
                        <option
                          key={template.id}
                          value={template.title}
                        >
                          {template.title}
                        </option>
                      )
                    )}
                  </select>
              </div>
              {selectedTemplate && (()=>{
                  const template=
                    docTemplates.find(
                      item =>
                        item.title ===
                        selectedTemplate
                    );

                  const fields=
                    template?.input_fields ||
                    [];

                  if (fields.length===0) {
                    return (
                      <div style={{
                        marginTop:12,
                        padding:12,
                        background:'#F9F8F5',
                        borderRadius:8,
                        border:
                          '1px solid #E8E0D4',
                        fontSize:12,
                        color:'#556B2F'
                      }}>
                        No additional information is
                        required. Verified prospect,
                        company and system information
                        will be filled automatically.
                      </div>
                    );
                  }

                  return (
                    <div style={{
                      marginTop:12,
                      padding:14,
                      background:'#F9F8F5',
                      borderRadius:8,
                      border:
                        '1px solid #E8E0D4'
                    }}>
                      <div style={{
                        fontSize:12,
                        fontWeight:700,
                        color:'#556B2F',
                        marginBottom:10,
                        textTransform:'uppercase',
                        letterSpacing:'.06em'
                      }}>
                        Document Information
                      </div>

                      {fields.map(field=>{
                        const value=
                          documentFieldValues[
                            field.field_key
                          ] || '';

                        const updateValue=
                          event=>{
                            setDocumentFieldValues(
                              current=>({
                                ...current,
                                [field.field_key]:
                                  event.target.value
                              })
                            );
                          };

                        if (
                          field.input_type ===
                          'textarea'
                        ) {
                          return (
                            <div
                              className="form-group"
                              key={field.field_key}
                            >
                              <label className="form-label">
                                {field.field_label}
                                {
                                  field.is_required
                                    ? ' *'
                                    : ' (optional)'
                                }
                              </label>

                              <textarea
                                className="form-control"
                                rows={3}
                                value={value}
                                required={
                                  field.is_required
                                }
                                onChange={
                                  updateValue
                                }
                              />

                              {field.help_text && (
                                <div className="form-hint">
                                  {field.help_text}
                                </div>
                              )}
                            </div>
                          );
                        }

                        if (
                          field.input_type ===
                          'select'
                        ) {
                          return (
                            <div
                              className="form-group"
                              key={field.field_key}
                            >
                              <label className="form-label">
                                {field.field_label}
                                {
                                  field.is_required
                                    ? ' *'
                                    : ' (optional)'
                                }
                              </label>

                              <select
                                className="form-control"
                                value={value}
                                required={
                                  field.is_required
                                }
                                onChange={
                                  updateValue
                                }
                              >
                                <option value="">
                                  — Select —
                                </option>

                                {
                                  (
                                    field.options ||
                                    []
                                  ).map(
                                    option=>(
                                      <option
                                        key={option}
                                        value={option}
                                      >
                                        {option}
                                      </option>
                                    )
                                  )
                                }
                              </select>

                              {field.help_text && (
                                <div className="form-hint">
                                  {field.help_text}
                                </div>
                              )}
                            </div>
                          );
                        }

                        return (
                          <div
                            className="form-group"
                            key={field.field_key}
                          >
                            <label className="form-label">
                              {field.field_label}
                              {
                                field.is_required
                                  ? ' *'
                                  : ' (optional)'
                              }
                            </label>

                            <input
                              className="form-control"
                              type={
                                field.input_type ===
                                  'number'
                                  ? 'number'
                                  : field.input_type ===
                                    'date'
                                    ? 'date'
                                    : field.input_type ===
                                      'email'
                                      ? 'email'
                                      : 'text'
                              }
                              min={
                                field.field_key ===
                                  'restriction_period_months'
                                  ? '1'
                                  : undefined
                              }
                              max={
                                field.field_key ===
                                  'restriction_period_months'
                                  ? '24'
                                  : undefined
                              }
                              value={value}
                              required={
                                field.is_required
                              }
                              onChange={
                                updateValue
                              }
                            />

                            {field.help_text && (
                              <div className="form-hint">
                                {field.help_text}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
                <div style={{background:'#E8F5EB',borderRadius:8,padding:10,fontSize:12,color:'#2E6B3A',marginTop:10}}>
                The document will be generated with {selected.full_name}'s details and sent to {selected.email} with an Accept/Decline link.
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowDocModal(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!selectedTemplate||sendingDoc} onClick={async()=>{
                if (!selectedTemplate) { alert('Please select a document'); return; }
                setSendingDoc(true);
                try {
                  const mergeFields = {
                    ...documentFieldValues
                  };

                  const selectedDocumentTemplate =
                    docTemplates.find(
                      template =>
                        template.id &&
                        template.title ===
                          selectedTemplate
                    );

                  if (!selectedDocumentTemplate) {
                    throw new Error(
                      'The selected secure document template is unavailable'
                    );
                  }

                  const missingRequiredField =
                    (
                      selectedDocumentTemplate
                        .input_fields || []
                    ).find(
                      field =>
                        field.is_required &&
                        !String(
                          documentFieldValues[
                            field.field_key
                          ] || ''
                        ).trim()
                    );

                  if (missingRequiredField) {
                    throw new Error(
                      'Complete the required document field: ' +
                      missingRequiredField
                        .field_label
                    );
                  }

                  const prepared =
                    await post(
                      '/documents/prepare',
                      {
                        recipient_type:
                          'prospect',
                        recipient_id:
                          selected.id,
                        template_id:
                          selectedDocumentTemplate.id,
                        hr_fields:
                          mergeFields
                      }
                    );

                  const secureDocument =
                    await get(
                      '/documents/' +
                      prepared.data.id
                    );

                  setSecureDocumentId(
                    prepared.data.id
                  );
                  setChecklistContent(
                    secureDocument.data
                      .content_snapshot
                  );
                  setChecklistTitle(
                    secureDocument.data
                      .template_title ||
                    selectedTemplate
                  );
                  setLastSentLink(null);
                  setShowDocModal(false);
                  setShowChecklist(true);
                } catch(e) { alert(e.message); }
                finally { setSendingDoc(false); }
              }}>{sendingDoc?'Generating...':'Generate & Preview'}</button>
            </div>
          </div>
        </div>
      )}
      {showEmploymentModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:520}}>
            <div className="modal-hdr">
              <span className="modal-title">Generate Employment Agreement</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowEmploymentModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#E8F5EB',borderRadius:8,padding:12,marginBottom:16,fontSize:12,color:'#2E6B3A'}}>
                The following fields will be auto-filled from the prospect record:<br/>
                <strong>Name, Position, Department, Salary, Probation Period, Work Location, Start Date, Reporting Manager</strong>
              </div>
              <div className="form-group">
                <label className="form-label">Steward Residential Address *</label>
                <textarea className="form-control" rows={2}
                  placeholder="Full residential address as it will appear on the agreement"
                  value={empForm.steward_residential_address}
                  onChange={e=>setEmpForm({...empForm,steward_residential_address:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Work Location *</label>
                <input className="form-control" value={empForm.work_location||''}
                  onChange={e=>setEmpForm({...empForm,work_location:e.target.value})}
                  placeholder="e.g. No. 3, S. Ade-Ojo Estate, Sawmill, Old Ife Road, Ibadan" />
                <div className="form-hint">Defaults to main Bloans address — update if different</div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Probation Notice Period *</label>
                  <select className="form-control" value={empForm.probation_notice_period}
                    onChange={e=>setEmpForm({...empForm,probation_notice_period:e.target.value})}>
                    <option value="1 week">1 week</option>
                    <option value="2 weeks">2 weeks</option>
                    <option value="1 month">1 month</option>
                  </select>
                  <div className="form-hint">Notice required from either party during probation</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Confirmed Notice Period *</label>
                  <select className="form-control" value={empForm.confirmed_steward_notice_period}
                    onChange={e=>setEmpForm({...empForm,confirmed_steward_notice_period:e.target.value})}>
                    <option value="2 weeks">2 weeks</option>
                    <option value="1 month">1 month</option>
                    <option value="2 months">2 months</option>
                    <option value="3 months">3 months</option>
                  </select>
                  <div className="form-hint">Notice required from Steward after confirmation</div>
                </div>
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowEmploymentModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={async()=>{
                if (!empForm.steward_residential_address) { alert('Residential address is required'); return; }
                try {
                  const r = await post('/prospects/'+selected.id+'/generate-employment-agreement', empForm);
                  setShowEmploymentModal(false);
                  setChecklistContent(r.data.merged_content);
                  setChecklistTitle('Letter of Appointment and Employment Agreement');
                  setShowChecklist(true);
                } catch(e) { alert(e.message); }
              }}>Generate & Preview</button>
            </div>
          </div>
        </div>
      )}

      {/* Non-Compete Modal */}
      {showNonCompeteModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:460}}>
            <div className="modal-hdr">
              <span className="modal-title">Generate Non-Compete Agreement</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowNonCompeteModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#E8F5EB',borderRadius:8,padding:12,marginBottom:16,fontSize:12,color:'#2E6B3A'}}>
                All other fields auto-fill from the prospect record. Only the restriction period needs to be specified.
              </div>
              <div className="form-group">
                <label className="form-label">Restriction Period (months) *</label>
                <select className="form-control" value={nonCompeteForm.restriction_period_months}
                  onChange={e=>setNonCompeteForm({...nonCompeteForm,restriction_period_months:e.target.value})}>
                  <option value="3">3 months</option>
                  <option value="6">6 months</option>
                  <option value="12">12 months</option>
                  <option value="18">18 months</option>
                  <option value="24">24 months</option>
                </select>
                <div className="form-hint">Applies to customer non-solicitation, workforce non-solicitation and limited non-compete clauses</div>
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowNonCompeteModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={async()=>{
                try {
                  const r = await post('/prospects/'+selected.id+'/generate-document', {
                    template_title: 'Non-Solicitation and Limited Non-Compete Agreement',
                    merge_fields: nonCompeteForm
                  });
                  setShowNonCompeteModal(false);
                  setChecklistContent(r.data.merged_content);
                  setChecklistTitle('Non-Solicitation and Limited Non-Compete Agreement');
                  setShowChecklist(true);
                } catch(e) { alert(e.message); }
              }}>Generate & Preview</button>
            </div>
          </div>
        </div>
      )}

      {/* IP Assignment Modal */}
      {showIPModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-hdr">
              <span className="modal-title">Generate IP Assignment Agreement</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowIPModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#E8F5EB',borderRadius:8,padding:12,marginBottom:16,fontSize:12,color:'#2E6B3A'}}>
                All other fields auto-fill from the prospect record. Only pre-existing IP needs to be declared.
              </div>
              <div className="form-group">
                <label className="form-label">Pre-Existing Material Schedule *</label>
                <textarea className="form-control" rows={4}
                  placeholder="List any intellectual property owned by the steward before employment that must be excluded from assignment. Enter 'None' if there is no pre-existing material."
                  value={ipForm.pre_existing_material_schedule}
                  onChange={e=>setIpForm({...ipForm,pre_existing_material_schedule:e.target.value})} />
                <div className="form-hint">This will appear in clause 5 of the agreement. If none, leave as "None".</div>
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowIPModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={async()=>{
                try {
                  const r = await post('/prospects/'+selected.id+'/generate-document', {
                    template_title: 'Intellectual Property and Work Product Assignment Agreement',
                    merge_fields: ipForm
                  });
                  setShowIPModal(false);
                  setChecklistContent(r.data.merged_content);
                  setChecklistTitle('Intellectual Property and Work Product Assignment Agreement');
                  setShowChecklist(true);
                } catch(e) { alert(e.message); }
              }}>Generate & Preview</button>
            </div>
          </div>
        </div>
      )}

      {/* Documents Sent Modal */}
      {showDocs && selected && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:600}}>
            <div className="modal-hdr">
              <span className="modal-title">Documents Sent to {selected.full_name}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowDocs(false)}>✕</button>
            </div>
            <div className="modal-body" style={{maxHeight:'60vh',overflowY:'auto'}}>
              {sentDocs.length === 0 ? (
                <div className="empty-state"><p>No documents sent yet.</p></div>
              ) : sentDocs.map(d=>(
                <div key={d.id} style={{background:'#F9F8F5',borderRadius:8,padding:14,marginBottom:10,border:'1px solid #E8E0D4'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                    <div style={{fontWeight:600,fontSize:13,color:'#3B2A21'}}>{d.document_title}</div>
                    <span style={{fontSize:11,padding:'2px 10px',borderRadius:20,fontWeight:700,
                      background:d.status==='accepted'?'#E8F5EB':d.status==='declined'?'#FDEAEA':'#FFF3DC',
                      color:d.status==='accepted'?'#2E6B3A':d.status==='declined'?'#8F2E24':'#8B5E0A'}}>
                      {
                      d.status==='accepted'
                        ? '✅ Accepted'
                        : d.status==='declined'
                          ? '❌ Declined'
                          : d.status==='viewed'
                            ? '👁 Opened'
                            : d.status==='sent'
                              ? '✉️ Sent'
                              : d.status
                    }
                    </span>
                  </div>
                  <div style={{fontSize:11,color:'#8A8A8A',lineHeight:1.7}}>
                    <div>
                      Sent to: {d.sent_to}
                    </div>
                    <div>
                      Sent: {d.sent_at
                        ? new Date(d.sent_at)
                            .toLocaleString(
                              'en-NG',
                              {
                                day:'numeric',
                                month:'short',
                                year:'numeric',
                                hour:'2-digit',
                                minute:'2-digit',
                                timeZone:'Africa/Lagos'
                              }
                            )
                        : 'Not sent'}
                      {d.sent_by_name
                        ? ' · By: ' + d.sent_by_name
                        : ''}
                    </div>
                    <div style={{
                      color:d.viewed_at
                        ? '#2E6B3A'
                        : '#8B5E0A',
                      fontWeight:600
                    }}>
                      {d.viewed_at
                        ? 'Opened: ' +
                          new Date(d.viewed_at)
                            .toLocaleString(
                              'en-NG',
                              {
                                day:'numeric',
                                month:'short',
                                year:'numeric',
                                hour:'2-digit',
                                minute:'2-digit',
                                timeZone:'Africa/Lagos'
                              }
                            )
                        : 'Not opened'}
                    </div>
                    {d.delivery_attempts != null && (
                      <div>
                        Delivery attempts: {
                          d.delivery_attempts
                        }
                        {d.last_delivery_error
                          ? ' · Latest delivery failed'
                          : d.delivery_accepted?.length
                            ? ' · Accepted by mail server'
                            : ''}
                      </div>
                    )}
                  </div>
                  {d.secure_workflow &&
                   (['sent', 'viewed'].includes(d.status) ||
                    (d.status === 'ready' && d.company_authorized === true && Boolean(d.last_delivery_error))) && (
                    <div style={{
                      marginTop:10,
                      display:'flex',
                      justifyContent:'flex-end'
                    }}>
                      <button
                        className="btn btn-outline btn-sm"
                        style={{
                          fontSize:10,
                          color:'#3B5BDB',
                          borderColor:'#3B5BDB'
                        }}
                        disabled={
                          resendingDocumentId ===
                          d.id
                        }
                        onClick={() =>
                          resendSecureDocument(d)
                        }
                      >
                        {resendingDocumentId === d.id
                          ? 'Sending...'
                          : d.status === 'ready' ? '✉️ Retry Delivery' : '✉️ Resend Email'}
                      </button>
                    </div>
                  )}
                  {d.acknowledged_at && (
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:6}}>
                      <div style={{fontSize:11,color:d.status==='accepted'?'#2E6B3A':'#8F2E24'}}>
                        {d.status==='accepted'?'✅ Accepted':'❌ Declined'} on {new Date(d.acknowledged_at).toLocaleDateString('en-NG',{day:'numeric',month:'long',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                      </div>
                      {d.status==='accepted' && d.document_content && (
                        <button className="btn btn-outline btn-sm" style={{fontSize:10}} onClick={()=>{
                          const w = window.open('','_blank');
                          w.document.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${d.document_title}</title>
<link href='https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap' rel='stylesheet'>
<style>
  body{font-family:Georgia,serif;color:#2C1F16;padding:40px;background:#fff;max-width:800px;margin:0 auto;}
  @media print{@page{margin:1cm;size:A4;}.no-print{display:none!important;}}
  .sig-block{border-top:2px solid #556B2F;margin-top:40px;padding-top:20px;display:grid;grid-template-columns:1fr 1fr;gap:40px;}
  .sig-line{border-bottom:1px solid #3B2A21;margin-bottom:6px;height:40px;}
  .sig-label{font-size:11px;color:#8A8A8A;text-transform:uppercase;letter-spacing:.05em;}
  .sig-value{font-size:13px;font-weight:600;color:#3B2A21;margin-top:4px;}
  .print-btn{display:block;margin:20px auto;padding:10px 24px;background:#556B2F;color:#fff;border:none;cursor:pointer;border-radius:6px;font-size:14px;}
</style></head><body>
<button class="print-btn no-print" onclick="window.print()">🖨️ Print / Save as PDF</button>
${d.document_content}
<div class="sig-block">
  <div>
    <div style="font-family:'Dancing Script',Georgia,serif;font-size:28px;color:#3B2A21;margin-bottom:4px;font-style:italic;">${selected.full_name}</div>
    <div class="sig-line"></div>
    <div class="sig-label">Prospect Signature</div>
    <div class="sig-value">${selected.full_name}</div>
    <div class="sig-value" style="font-size:11px;color:#8A8A8A;">Accepted electronically on ${new Date(d.acknowledged_at).toLocaleDateString('en-NG',{day:'numeric',month:'long',year:'numeric'})}</div>
  </div>
  <div>
    <div style="font-family:'Dancing Script',Georgia,serif;font-size:28px;color:#3B2A21;margin-bottom:4px;font-style:italic;">Doris Gideon-Ayantola</div>
    <div class="sig-line"></div>
    <div class="sig-label">Authorised Signatory</div>
    <div class="sig-value">Doris Gideon-Ayantola</div>
    <div class="sig-value" style="font-size:11px;color:#8A8A8A;">Managing Director, Bloans Creations Ltd.</div>
  </div>
</div>
<p style="font-size:10px;color:#8A8A8A;text-align:center;margin-top:30px;border-top:1px solid #eee;padding-top:12px;">
  Bloans Creations Ltd. · No. 3, S. Ade-Ojo Estate, Sawmill, Old Ife Road, Ibadan, Oyo State, Nigeria<br/>
  Document Reference: ${d.id.substring(0,8).toUpperCase()} · Token-verified electronic acceptance
</p>
</body></html>`);
                          w.document.close();
                        }}>🖨️ Print</button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowDocs(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {/* Checklist/Document Preview Modal */}
      {showChecklist && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:800,width:'95vw'}}>
            <div className="modal-hdr">
              <span className="modal-title">Document Preview</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowChecklist(false)}>✕</button>
            </div>
            <div className="modal-body" style={{maxHeight:'75vh',overflowY:'auto',padding:0}}>
              <div dangerouslySetInnerHTML={{__html:checklistContent}} />
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowChecklist(false)}>Close</button>
              {selected && selected.email && (
                <button
                  className="btn btn-primary"
                  disabled={
                    sendingDoc ||
                    !secureDocumentId
                  }
                  onClick={async()=>{
                    if (!secureDocumentId) {
                      alert(
                        'Prepare this document through the secure Select Document workflow before sending it.'
                      );
                      return;
                    }

                    if (
                      !window.confirm(
                        'Authorize the company signature and send this protected document to ' +
                        selected.full_name +
                        '?'
                      )
                    ) {
                      return;
                    }

                    setSendingDoc(true);

                    try {
                      const result =
                        await post(
                          '/documents/' +
                          secureDocumentId +
                          '/authorize-send',
                          {
                            confirm_company_signature:
                              true
                          }
                        );

                      if (result.review_url) {
                        setLastSentLink(
                          result.review_url
                        );
                      }

                      setShowChecklist(false);
                      setSecureDocumentId(null);
                      load();
                      loadSelected(selected.id);

                      alert(
                        'The protected document was authorized and sent to ' +
                        selected.email +
                        '.'
                      );
                    } catch(e) {
                      alert(e.message);
                    } finally {
                      setSendingDoc(false);
                    }
                  }}
                >
                  {
                    sendingDoc
                      ? 'Authorizing & Sending...'
                      : secureDocumentId
                        ? 'Authorize & Send to ' +
                          selected.full_name
                        : 'Use Secure Document Workflow'
                  }
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* GuarantorDetailPanel moved above */}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:620,width:'92vw'}}>
            <div className="modal-hdr">
              <span className="modal-title">New Prospect</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'80vh',overflowY:'auto'}}>
              <div className="form-row">
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Full Legal Name *</label>
                  <input className="form-control" value={form.full_name}
                    onChange={e=>setForm({...form,full_name:e.target.value})} />
                </div>
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Email *</label>
                  <input type="email" className="form-control" value={form.email}
                    onChange={e=>setForm({...form,email:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-control" value={form.phone}
                    onChange={e=>setForm({...form,phone:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Position *</label>
                  <select className="form-control" value={form.position}
                    onChange={e=>{
                      const jd = jobDescriptions.find(j=>j.job_title===e.target.value);
                      setForm({...form,
                        position:e.target.value,
                        monthly_salary: e.target.value.trim().toLowerCase()==='inventory auditor'
                          ? '50000' : (form.position.trim().toLowerCase()==='inventory auditor' ? '' : form.monthly_salary),
                        department: jd?.department||form.department,
                        reports_to: e.target.value.trim().toLowerCase()==='inventory auditor'
                          ? 'Managing Director' : (jd?.reports_to||form.reports_to),
                        employment_category: e.target.value.trim().toLowerCase()==='inventory auditor'
                          ? 'Contract' : (form.employment_category||'Full-time')
                      });
                    }}>
                    <option value="">Select position...</option>
                    {!jobDescriptions.some(jd=>String(jd.job_title||'').trim().toLowerCase()==='inventory auditor') &&
                      <option value="Inventory Auditor">Inventory Auditor</option>}
                    {jobDescriptions.map(jd=>(
                      <option key={jd.id} value={jd.job_title}>{jd.job_title}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Department</label>
                  <input className="form-control" value={form.department}
                    onChange={e=>setForm({...form,department:e.target.value})} />
                </div>
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Reports To</label>
                  <input className="form-control" value={form.reports_to}
                    readOnly={form.position.trim().toLowerCase() === 'inventory auditor'}
                    onChange={e=>setForm({...form,reports_to:e.target.value})} />
                </div>
              </div>
              {form.position.trim().toLowerCase() === 'inventory auditor' &&
                <p style={{color:'#556B2F',fontSize:12,margin:'0 0 12px'}}>
                  Offer template: Inventory Auditor Offer Letter. Reports directly to the Managing Director.
                </p>}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input type="date" className="form-control" value={form.proposed_start_date}
                    onChange={e=>setForm({...form,proposed_start_date:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">{form.position.trim().toLowerCase()==='inventory auditor' ? 'Monthly Contract Remuneration (₦)' : 'Monthly Salary'}</label>
                  <input type="number" className="form-control" value={form.monthly_salary}
                    onChange={e=>setForm({...form,monthly_salary:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Offer Expires</label>
                  <input type="date" className="form-control" value={form.expiry_date}
                    onChange={e=>setForm({...form,expiry_date:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Probation</label>
                  <select className="form-control" value={form.probation_period}
                    onChange={e=>setForm({...form,probation_period:e.target.value})}>
                    <option>1 month</option>
                    <option>3 months</option>
                    <option>6 months</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Employment Type</label>
                  <select className="form-control" value={form.employment_category}
                    disabled={form.position.trim().toLowerCase()==='inventory auditor'}
                    onChange={e=>setForm({...form,employment_category:e.target.value})}>
                    {form.position.trim().toLowerCase() === 'inventory auditor' &&
                      <option value="">Select approved category...</option>}
                    <option>Full-time</option>
                    <option>Part-time</option>
                    <option>Contract</option>
                  </select>
                </div>
              </div>
              {form.position.trim().toLowerCase() === 'inventory auditor' && (
                <div style={{background:'#F9F8F5',padding:14,borderRadius:8,marginBottom:12}}>
                  <p style={{fontSize:12,fontWeight:700,margin:'0 0 8px'}}>
                    Complete these approved terms before sending the Inventory Auditor offer.
                  </p>
                  {[
                    ['auditor_payment_terms','Payment timing and partial first month'],
                    ['auditor_assessment_terms','Compatibility assessment: duration, attendance and pay, or state none'],
                    ['auditor_notice_benefits_terms','Notice terms and applicable benefits'],
                  ].map(([key,label])=>(
                    <div className="form-group" key={key}>
                      <label className="form-label">{label} *</label>
                      <textarea className="form-control" rows={2}
                        value={form[key]}
                        onChange={e=>setForm({...form,[key]:e.target.value})} />
                    </div>
                  ))}
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={form.notes}
                  onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={create} disabled={saving}>
                {saving?'Creating...':'Create Prospect'}
              </button>
            </div>
          </div>
        </div>
      )}

        <ProspectOnboardingWorkflow
          selected={selected}
          showImportModal={showImportModal}
          setShowImportModal={setShowImportModal}
          importingDocument={importingDocument}
          setImportingDocument={setImportingDocument}
          importForm={importForm}
          setImportForm={setImportForm}
          showActivationModal={showActivationModal}
          setShowActivationModal={setShowActivationModal}
          activationSaving={activationSaving}
          setActivationSaving={setActivationSaving}
          activationForm={activationForm}
          setActivationForm={setActivationForm}
          reloadSelected={()=>loadSelected(selected.id)}
          reloadList={load}
        />

      {showGuarantorModal && selected && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:440}}>
            <div className="modal-hdr">
              <span className="modal-title">{selected.guarantor ? 'Edit / Resend Guarantor' : 'Send Guarantor Invitation'}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowGuarantorModal(false)}>X</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#FFF3DC',borderRadius:8,padding:10,fontSize:12,color:'#8B5E0A',marginBottom:16}}>
                {selected.guarantor ? 'Update details or send the link to a different email address.' : 'Send this directly to the guarantor, not the applicant.'}
              </div>
              <div className="form-group">
                <label className="form-label">Guarantor Full Name *</label>
                <input className="form-control" value={guarantorForm.guarantor_name}
                  onChange={e=>setGuarantorForm({...guarantorForm,guarantor_name:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Guarantor Email *</label>
                <input type="email" className="form-control" value={guarantorForm.guarantor_email}
                  onChange={e=>setGuarantorForm({...guarantorForm,guarantor_email:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Guarantor Phone</label>
                <input className="form-control" value={guarantorForm.guarantor_phone}
                  onChange={e=>setGuarantorForm({...guarantorForm,guarantor_phone:e.target.value})} />
              </div>
              {selected.guarantor && (
                <div className="form-group">
                  <label className="form-label">Send to Alternative Email (optional)</label>
                  <input type="email" className="form-control" value={guarantorForm.alternative_email||''}
                    onChange={e=>setGuarantorForm({...guarantorForm,alternative_email:e.target.value})}
                    placeholder="Leave blank to use the email above" />
                  <div className="form-hint">Use this if the original email bounced or the guarantor prefers a different address</div>
                </div>
              )}
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowGuarantorModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={()=>sendGuarantor(selected.id)} disabled={saving}>
                {saving?'Sending...':'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showVerify && selected && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-hdr">
              <span className="modal-title">Verify Guarantor</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowVerify(false)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Decision *</label>
                <select className="form-control" value={verifyForm.decision}
                  onChange={e=>setVerifyForm({...verifyForm,decision:e.target.value})}>
                  <option value="">Select decision</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="correction_required">Correction Required</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Verification Notes</label>
                <textarea className="form-control" rows={3} value={verifyForm.notes}
                  onChange={e=>setVerifyForm({...verifyForm,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowVerify(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={()=>verifyGuarantor(selected.id)} disabled={saving}>
                {saving?'Saving...':'Record Decision'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GuarantorDetailPanel({ prospectId, guarantorSummary }) {
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  const load = () => {
    if (detail) { setExpanded(!expanded); return; }
    setLoading(true);
    get('/prospects/'+prospectId+'/guarantor')
      .then(d => { setDetail(d.data); setExpanded(true); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const fmt = v => v || <span style={{color:'#C0B8B0',fontStyle:'italic'}}>Not provided</span>;
  const fmtDate = v => v ? new Date(v).toLocaleDateString('en-NG',{day:'numeric',month:'long',year:'numeric'}) : null;

  const statusColor = guarantorSummary?.status === 'approved' ? '#2E6B3A' :
    guarantorSummary?.status === 'submitted' ? '#8B5E0A' : '#8A8A8A';
  const statusBg = guarantorSummary?.status === 'approved' ? '#E8F5EB' :
    guarantorSummary?.status === 'submitted' ? '#FFF3DC' : '#F5F5F5';

  const Row = ({label, value}) => (
    <div style={{display:'flex',padding:'7px 0',borderBottom:'1px solid #F2EFE7',fontSize:13}}>
      <div style={{width:'40%',color:'#8A8A8A',fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.04em'}}>{label}</div>
      <div style={{flex:1,color:'#2C1F16'}}>{fmt(value)}</div>
    </div>
  );

  return (
    <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,padding:20}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:expanded?16:0}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <div style={{fontSize:11,fontWeight:700,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.08em'}}>Guarantor</div>
          <span style={{fontSize:10,padding:'3px 10px',borderRadius:20,fontWeight:700,background:statusBg,color:statusColor}}>
            {guarantorSummary?.status || 'none'}
          </span>
          {guarantorSummary?.full_name && (
            <span style={{fontSize:12,color:'#3B2A21',fontWeight:600}}>{guarantorSummary.full_name}</span>
          )}
        </div>
        <button onClick={load} style={{fontSize:11,padding:'5px 12px',borderRadius:6,
          border:'1px solid #E8E0D4',background:'#F9F8F5',cursor:'pointer',color:'#556B2F'}}>
          {loading ? 'Loading...' : expanded ? '▲ Hide Details' : '▼ View Details'}
        </button>
      </div>

      {expanded && detail && (
        <div>
          {detail.submitted_at && (
            <div style={{background:statusBg,borderRadius:8,padding:'8px 14px',
              marginBottom:16,fontSize:12,color:statusColor}}>
              {detail.status === 'approved' ? '✅' : '⏳'} Form submitted on {fmtDate(detail.submitted_at)}
              {detail.verified_at && ` · Verified on ${fmtDate(detail.verified_at)}`}
            </div>
          )}
          <div style={{fontWeight:700,fontSize:11,color:'#556B2F',textTransform:'uppercase',
            letterSpacing:'.08em',marginBottom:8,marginTop:4}}>Personal Information</div>
          <Row label="Full Name" value={detail.full_name} />
          <Row label="Email" value={detail.email} />
          <Row label="Phone" value={detail.phone} />
          <Row label="Address" value={detail.address} />
          <Row label="Relationship to Candidate" value={detail.relationship} />
          <Row label="Years Known" value={detail.years_known} />
          <Row label="Occupation" value={detail.occupation} />
          <Row label="Employer" value={detail.employer} />
          <Row label="ID Number (NIN)" value={detail.nin} />
          {detail.uploaded_documents && Object.keys(detail.uploaded_documents).length > 0 && (
            <div style={{marginTop:16}}>
              <div style={{fontWeight:700,fontSize:11,color:'#556B2F',textTransform:'uppercase',
                letterSpacing:'.08em',marginBottom:8}}>Uploaded Documents</div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {Object.entries(detail.uploaded_documents).map(([key]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() =>
                        openProtectedFile(
                          '/prospects/guarantors/' +
                          detail.id +
                          '/documents/' +
                          encodeURIComponent(key)
                        ).catch(error =>
                          window.alert(error.message)
                        )
                      }
                      style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',
                        borderRadius:8,background:'#F2EFE7',color:'#556B2F',fontSize:12,
                        border:'1px solid #C8BFA8',cursor:'pointer'}}>
                      📎 {key.replace(/_/g,' ')}
                    </button>
                  ))}
              </div>
            </div>
          )}
          {detail.verification_notes && (
            <div style={{marginTop:12,background:'#F9F8F5',borderRadius:8,padding:12,fontSize:12,color:'#555'}}>
              <strong>Verification Notes:</strong> {detail.verification_notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
